import { useMemo } from 'react'
import type { CSSProperties, MouseEvent } from 'react'
import type { MarketingCard as MarketingCardConfig, MarketingCardsGlobalStyle } from '../config'
import { inlineHtmlAccessibleText, sanitizeInlineHtml } from '../../../common/sanitize/inline-html'
import {
  hexToRgba,
  isProtocolHandlerHref,
  resolveCardColors,
  safeCardHref,
  safeCardLinkHref,
  safeCssUrl,
} from '../marketingCards'
import { safeCssColor } from '../../../common/css-safe'

interface MarketingCardProps {
  card: MarketingCardConfig
  /** Stable card id (authored `id` or positional fallback) — reported in events. */
  cardId: string
  /** 1-based insertion slot — with `cardId`, the impression de-dupe key. */
  ordinal: number
  globalStyle: MarketingCardsGlobalStyle
  onCtaClick?: (cardId: string, ordinal: number, url: string, target: Element) => void
}

/**
 * One promo card in the explorer results grid (#1145). Port of ckit's
 * `templates/tools/marketing-cards/card.php`:
 *
 * - Colors are baked onto the wrapper as `--hfse-mc-*` custom properties
 *   (the same inline-custom-property mechanism the grid itself uses for its
 *   column vars); explorer.css reads them. Operator-chosen colors apply in
 *   both themes, exactly as in WP, so there is nothing theme-dependent to
 *   dual-emit — every token still passes `safeCssColor` at emission.
 * - Whole-card link is a stretched-link SIBLING anchor, never a wrapper, so
 *   the CTA button anchor is never nested inside another anchor. When a real
 *   CTA exists the overlay is decorative (aria-hidden, unfocusable); with no
 *   button the overlay is the only link and keeps a label + focus.
 * - `rel="noopener"` is emitted on every link unconditionally (brief AC 2);
 *   `target="_blank"` only when `newTab` is on AND the link is a page — a
 *   mailto:/tel: link (#1163) hands off to a handler and never gets one.
 * - Accessible names (#1162): an authored `accessibleLabel` names the card
 *   region and its whole-card link; blank keeps WP's text fallbacks.
 * - Body defaults to plain text; line breaks are preserved via CSS
 *   `white-space: pre-line`. With the per-card `bodyRichText` opt-in (#1163)
 *   the body renders through the minimal inline allowlist
 *   (`sanitizeInlineHtml`: bold/em/line breaks, no attributes), sanitized at
 *   the render boundary on BOTH the SSR and CSR paths — content-widget
 *   precedent, so a stored payload never reaches a visitor even if Central's
 *   copy was written by a compromised dashboard login. pre-line still
 *   applies, so authored newlines keep working alongside `<br>`.
 */
export default function MarketingCard({ card, cardId, ordinal, globalStyle, onCtaClick }: MarketingCardProps) {
  const colors = resolveCardColors(card, globalStyle)
  // The LINK admits mailto:/tel: (#1163); image/asset URLs stay https/relative.
  const link = safeCardLinkHref(card.linkUrl)
  const imageUrl = safeCardHref(card.imageUrl)
  const bgImage = safeCssUrl(card.bgImageUrl)
  const eyebrow = card.eyebrow ?? ''
  const title = card.title ?? ''
  const body = card.body ?? ''
  const ctaText = card.ctaText ?? ''
  const accessibleLabel = card.accessibleLabel ?? ''
  const richBody = card.bodyRichText === true
  // Sanitized at the render boundary on both paths: SSR emits this string and
  // the client hydrates the same one (a pure function of the stored body).
  // The ACCESSIBLE projection rides along for the aria-label fallback — the
  // label variant, not the emptiness one: emphasis tags are not word
  // boundaries and emoji joiners must survive (fresh-pass finding). The
  // is-this-card-content checks stay on inlineHtmlVisibleText upstream.
  const { bodyHtml, richBodyText } = useMemo(
    () => (richBody
      ? { bodyHtml: sanitizeInlineHtml(body), richBodyText: inlineHtmlAccessibleText(body) }
      : { bodyHtml: '', richBodyText: '' }),
    [richBody, body],
  )

  const hasButton = link !== '' && ctaText !== ''
  const wholeCardLink = card.linkWholeCard === true && link !== ''
  // Accessible names (#1162). An authored label, when set, names both the
  // card region and its whole-card link: it exists for image-only cards,
  // whose message is in the pixels and would otherwise reach assistive tech
  // as the generic "Promotion". Blank keeps the WP fallbacks — eyebrow for
  // the region; title → eyebrow → body snippet for the link, so the
  // whole-card link exposes real content even when the card carries no
  // title or eyebrow. The inline <img> stays alt="" on purpose: the region
  // and the link already carry the name, and a third announcement of the
  // same text is noise, not access.
  const aria = accessibleLabel !== '' ? accessibleLabel : eyebrow !== '' ? eyebrow : 'Promotion'
  // The accessible-name fallback must voice PLAIN text, and only text that
  // actually RENDERS: the projection sanitizes first (round 2, finding 3),
  // so a removed element's contents (`<script>Secret</script>`) are never
  // spoken for a card that doesn't show them.
  const bodyText = richBody ? richBodyText : body
  const bodySnippet = bodyText.length > 60 ? `${bodyText.slice(0, 57)}…` : bodyText
  const fallbackLabel =
    title !== '' ? title : eyebrow !== '' ? eyebrow : bodySnippet !== '' ? bodySnippet : 'Promotion'
  const linkLabel = accessibleLabel !== '' ? accessibleLabel : fallbackLabel

  const style: Record<string, string> = {
    '--hfse-mc-bg': safeCssColor(colors.bgColor, '#0A386B'),
    '--hfse-mc-text': safeCssColor(colors.textColor, '#ffffff'),
    '--hfse-mc-btn-bg': safeCssColor(colors.buttonBg, '#ffffff'),
    '--hfse-mc-btn-text': safeCssColor(colors.buttonText, '#0A386B'),
  }
  if (bgImage !== '') {
    style['--hfse-mc-bg-image'] = `url("${bgImage}")`
    style['--hfse-mc-bg-scrim'] = hexToRgba(card.bgOverlayColor, card.bgOverlayOpacity / 100)
  }

  const classes = ['hfse-marketing-card']
  if (wholeCardLink) classes.push('hfse-mc-has-cardlink')
  if (bgImage !== '') classes.push('hfse-mc-has-bg-image')

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    onCtaClick?.(cardId, ordinal, link, e.currentTarget)
  }
  // "Open in new tab" only means something for a page. A mailto:/tel: link
  // hands off to the mail or phone handler, and `target="_blank"` on one
  // leaves a stray blank tab behind in some browsers — so the flag is
  // ignored for those schemes (a deliberate narrowing of WP's card.php,
  // which emits it for every scheme).
  const targetProps = card.newTab && !isProtocolHandlerHref(link) ? { target: '_blank' as const } : {}

  return (
    <div
      className={classes.join(' ')}
      data-hr-mc-id={cardId}
      data-hr-mc-ordinal={ordinal}
      role="complementary"
      aria-label={aria}
      style={style as CSSProperties}
    >
      <div className="hfse-mc-inner">
        {wholeCardLink && (
          <a
            className="hfse-mc-cardlink"
            href={link}
            rel="noopener"
            {...targetProps}
            data-hr-mc-id={cardId}
            onClick={handleClick}
            {...(hasButton ? { 'aria-hidden': true, tabIndex: -1 } : { 'aria-label': linkLabel })}
          />
        )}
        {imageUrl !== '' && (
          <div className="hfse-mc-media">
            <img src={imageUrl} alt="" loading="lazy" />
          </div>
        )}
        <div className="hfse-mc-body">
          {eyebrow !== '' && <span className="hfse-mc-eyebrow">{eyebrow}</span>}
          {title !== '' && <h3 className="hfse-mc-title">{title}</h3>}
          {richBody
            ? bodyHtml !== '' && (
              <div className="hfse-mc-copy" dangerouslySetInnerHTML={{ __html: bodyHtml }} />
            )
            : body !== '' && <div className="hfse-mc-copy">{body}</div>}
          {hasButton && (
            <a
              className="hfse-mc-cta"
              href={link}
              rel="noopener"
              {...targetProps}
              data-hr-mc-id={cardId}
              onClick={handleClick}
            >
              {ctaText}
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
